import React, { useState } from 'react';
import { MassDataFiltersProps } from './mass-data-filters.types';
import { Flex } from '../../../../common/common-components.styled';
import {
  ClearAllFiltersButton,
  Filter,
  FilterButton,
  FilterContainer,
  FilterInput,
  PickersContainer
} from '../../../../components/filter-items';
import useLeasingOffersFilterData from '../hooks/use-leasing-offers-filter-data';
import { AnyObject, LookupResponse } from '../../../../common/interfaces';
import { convertDateToServer } from '../../../../utils/convert-date-to-server';

const MassDataFilters = ({ sessionFilter, setSessionFilter, goToFirstPage, vehicleOfferId }: MassDataFiltersProps) => {
  const [isOpen, setOpen] = useState('');

  const { filterData } = useLeasingOffersFilterData(vehicleOfferId || '');

  const nameLabel = sessionFilter?.filterForClient[MassDataFiltersValues.Name];
  const leasingBankLabel = sessionFilter?.filterForClient[MassDataFiltersValues.LeasingBank];
  const durationLabel = sessionFilter?.filterForClient[MassDataFiltersValues.Duration];
  const distanceLabel = sessionFilter?.filterForClient[MassDataFiltersValues.Distance];
  const createdOnLabel = sessionFilter?.filterForClient[MassDataFiltersValues.CreatedOn];
  const createdByLabel = sessionFilter?.filterForClient[MassDataFiltersValues.CreatedBy];
  const validFromLabel = sessionFilter?.filterForClient[MassDataFiltersValues.ValidFrom];
  const validToLabel = sessionFilter?.filterForClient[MassDataFiltersValues.ValidTo];
  const statusLabel = sessionFilter?.filterForClient[MassDataFiltersValues.Status];

  const convertHeaderToCrmName = (title: string = '') => {
    switch (title.toLowerCase()) {
      case 'id':
        return MassDataFiltersValues.Name;
      case 'leasinggesellschaft':
        return MassDataFiltersValues.LeasingBank;
      case 'laufzeit':
        return MassDataFiltersValues.Duration;
      case 'laufleistung':
        return MassDataFiltersValues.Distance;
      case 'angefragt am':
        return MassDataFiltersValues.CreatedOn;
      case 'angefragt von':
        return MassDataFiltersValues.CreatedBy;
      case 'gültig von':
        return MassDataFiltersValues.ValidFrom;
      case 'gültig bis':
        return MassDataFiltersValues.ValidTo;
      case 'status':
        return MassDataFiltersValues.Status;

      default:
        return '';
    }
  };

  const saveFilter = (title: string, serverValue: string | null | AnyObject, clientValue: string | null) => {
    setSessionFilter(
      { [convertHeaderToCrmName(title)]: serverValue },
      { [convertHeaderToCrmName(title)]: clientValue }
    );
  };

  const clearFilter = (title?: string) => {
    setOpen('');
    goToFirstPage();

    if (!title) {
      setSessionFilter(emptyFilterState, emptyFilterState);
      return;
    }

    saveFilter(title, null, null);
  };

  const isDateFilter = (title: string) => {
    return (
      convertHeaderToCrmName(title) === MassDataFiltersValues.CreatedOn ||
      convertHeaderToCrmName(title) === MassDataFiltersValues.ValidFrom ||
      convertHeaderToCrmName(title) === MassDataFiltersValues.ValidTo
    );
  };

  const handleInput = (title: string, value: string | number | LookupResponse) => {
    setOpen('');
    goToFirstPage();

    if (convertHeaderToCrmName(title) === MassDataFiltersValues.LeasingBank) {
      const company = filterData?.new_leasingbank.find(companyItem => companyItem.id === (value as LookupResponse).id);
      if (company) {
        saveFilter(title, company.id, company.name);
      }
      return;
    }
    if (convertHeaderToCrmName(title) === MassDataFiltersValues.Duration) {
      const duration = filterData?.uds_laufzeit.find(duration => (value as string).includes(String(duration)));
      if (duration) {
        saveFilter(title, String(duration), value as string);
      }
      return;
    }
    if (convertHeaderToCrmName(title) === MassDataFiltersValues.Distance) {
      const distance = filterData?.uds_laufleistungpa.find(
        distance => String(distance) === (value as string).split(' km')[0].replace('.', '')
      );
      if (distance) {
        saveFilter(title, String(distance), value as string);
      }
      return;
    }
    if (convertHeaderToCrmName(title) === MassDataFiltersValues.CreatedBy) {
      const createdBy = filterData?.uds_portal_userid.find(user => user.id === (value as LookupResponse).id);
      if (createdBy) {
        saveFilter(title, createdBy.id, createdBy.name);
      }
      return;
    }
    if (isDateFilter(title)) {
      return saveFilter(title, convertDateToServer(value as string), value as string);
    }
    if (convertHeaderToCrmName(title) === MassDataFiltersValues.Status) {
      const status = filterData?.uds_angebotstatecode.find(item => item.value === value);
      if (status) {
        saveFilter(title, String(status.value), status.label);
      }
      return;
    }

    saveFilter(title, value as string, value as string);
  };

  return (
    <Flex align='center' bottom='30' wrap='wrap'>
      <Filter title='ID' isOpen={isOpen} value={nameLabel} toggleFilterIsOpen={setOpen} clearFilter={clearFilter}>
        <FilterContainer header='ID' onClose={setOpen}>
          <FilterInput
            title='ID'
            handleInput={handleInput}
            dropListData={filterData?.uds_name || []}
            withoutSearchButton
          />
        </FilterContainer>
      </Filter>

      <Filter
        title='Leasinggesellschaft'
        isOpen={isOpen}
        value={leasingBankLabel}
        toggleFilterIsOpen={setOpen}
        clearFilter={clearFilter}
      >
        <FilterContainer header='Leasinggesellschaft' onClose={setOpen}>
          <FilterInput
            title='Leasinggesellschaft'
            handleInput={handleInput}
            dropListData={filterData?.new_leasingbank || []}
            dropListDataWithId
            withoutSearchButton
          />
        </FilterContainer>
      </Filter>

      <Filter
        title='Laufzeit'
        isOpen={isOpen}
        value={durationLabel}
        toggleFilterIsOpen={setOpen}
        clearFilter={clearFilter}
      >
        <FilterContainer header='Laufzeit' onClose={setOpen}>
          <FilterInput
            title='Laufzeit'
            handleInput={handleInput}
            dropListData={filterData?.uds_laufzeit.map(item => `${item} Monate`) || []}
            withoutSearchButton
          />
        </FilterContainer>
      </Filter>

      <Filter
        title='Laufleistung'
        isOpen={isOpen}
        value={distanceLabel}
        toggleFilterIsOpen={setOpen}
        clearFilter={clearFilter}
      >
        <FilterContainer header='Laufleistung' onClose={setOpen}>
          <FilterInput
            title='Laufleistung'
            handleInput={handleInput}
            dropListData={filterData?.uds_laufleistungpa.map(item => `${item.toLocaleString('de-DE')} km`) || []}
            withoutSearchButton
          />
        </FilterContainer>
      </Filter>

      <Filter
        value={createdOnLabel}
        title='Angefragt am'
        isOpen={isOpen}
        toggleFilterIsOpen={setOpen}
        clearFilter={clearFilter}
      >
        <FilterContainer header='Angefragt am' onClose={() => setOpen('')}>
          <PickersContainer title='Angefragt am' value={createdOnLabel} handleInput={handleInput} />
        </FilterContainer>
      </Filter>

      <Filter
        title='Angefragt von'
        isOpen={isOpen}
        value={createdByLabel}
        toggleFilterIsOpen={setOpen}
        clearFilter={clearFilter}
      >
        <FilterContainer header='Angefragt von' onClose={setOpen}>
          <FilterInput
            title='Angefragt von'
            handleInput={handleInput}
            dropListData={filterData?.uds_portal_userid || []}
            dropListDataWithId
            withoutSearchButton
          />
        </FilterContainer>
      </Filter>

      <Filter
        value={validFromLabel}
        title='Gültig von'
        isOpen={isOpen}
        toggleFilterIsOpen={setOpen}
        clearFilter={clearFilter}
      >
        <FilterContainer header='Gültig von' onClose={() => setOpen('')}>
          <PickersContainer title='Gültig von' value={validFromLabel} handleInput={handleInput} />
        </FilterContainer>
      </Filter>

      <Filter
        value={validToLabel}
        title='Gültig bis'
        isOpen={isOpen}
        toggleFilterIsOpen={setOpen}
        clearFilter={clearFilter}
      >
        <FilterContainer header='Gültig bis' onClose={() => setOpen('')}>
          <PickersContainer title='Gültig bis' value={validToLabel} handleInput={handleInput} />
        </FilterContainer>
      </Filter>

      <Filter title='Status' isOpen={isOpen} value={statusLabel} toggleFilterIsOpen={setOpen} clearFilter={clearFilter}>
        <FilterContainer header='Status' onClose={() => setOpen('')}>
          {(filterData?.uds_angebotstatecode || []).map(item => {
            return (
              <FilterButton
                key={item.value}
                title='Status'
                handleInput={handleInput}
                label={item.label}
                value={item.value}
              />
            );
          })}
        </FilterContainer>
      </Filter>

      <ClearAllFiltersButton filterState={sessionFilter?.filterForClient || {}} clearFilter={clearFilter} />
    </Flex>
  );
};

export enum MassDataFiltersValues {
  Name = 'uds_name',
  LeasingBank = 'new_leasingbank',
  Duration = 'uds_laufzeit',
  Distance = 'uds_laufleistungpa',
  CreatedOn = 'createdon',
  CreatedBy = 'uds_portal_userid',
  ValidFrom = 'uds_validfrom',
  ValidTo = 'uds_validuntil',
  Status = 'uds_angebotstatecode'
}

export const emptyFilterState = {
  [MassDataFiltersValues.Name]: null,
  [MassDataFiltersValues.LeasingBank]: null,
  [MassDataFiltersValues.Duration]: null,
  [MassDataFiltersValues.Distance]: null,
  [MassDataFiltersValues.CreatedOn]: null,
  [MassDataFiltersValues.CreatedBy]: null,
  [MassDataFiltersValues.ValidFrom]: null,
  [MassDataFiltersValues.ValidTo]: null,
  [MassDataFiltersValues.Status]: null
};

export default MassDataFilters;
